'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { EnabledToolsConfig } from '@/lib/agents/defaults';
import { DEFAULT_PLATFORM_MODEL } from '@/lib/models/catalog';
import { useAuth } from '@/components/auth-provider';
import { useTranslation } from '@/components/i18n-provider';
import { useFullscreenNav } from '@/components/workspace/fullscreen-nav';
import { useWorkspaceActions } from '@/components/workspace/store';
import { fetchPersonalAgent } from '@/hooks/use-personal-agent';
import { Spinner } from '@/components/ui/spinner';
import { useAnalytics } from '@/hooks/use-analytics';
import { AttachEntityPicker, type AttachedEntity } from '@/components/create/attach-entity-picker';
import {
  PenSquare,
  Image as ImageIcon,
  Link as LinkIcon,
  Music,
  X,
  Upload,
  ArrowLeft,
  Briefcase,
  Globe,
  ChevronDown,
  ChevronUp,
  Box,
  Sparkles,
  Check,
} from 'lucide-react';
import { BotIcon as Bot } from '@/components/icons/bot-icon';

/**
 * Named-export form components reused by:
 *   - the legacy /create route (now retired in favour of the
 *     workspace `?dialog=create` signal),
 *   - the workspace `CreateDialog` component which mounts these
 *     three forms inside an in-canvas dialog so the user never
 *     leaves the workspace to author a new post / channel / agent.
 *
 * The page-level `CreateContent` / `CreatePage` wrapper that
 * supplied the tabbed shell was deleted along with the standalone
 * route — `CreateDialog` provides its own shell now.
 */

// OG Metadata interface
interface OGMetadata {
  title?: string;
  description?: string;
  image?: string;
  imageLocal?: string;
  siteName?: string;
  embedType?: string;
  embedUrl?: string;
}

// Channel type
interface Channel {
  id: string;
  slug: string;
  name: string;
  description?: string;
  memberCount?: number;
}

// Create Post Form
export function CreatePostForm({ preselectedChannel, initialContent, fromMessageId, quotePostId, attachWidgetContentId, attachWidgetName, onSuccess }: { preselectedChannel?: string; initialContent?: string; fromMessageId?: string; quotePostId?: string; attachWidgetContentId?: string; attachWidgetName?: string; onSuccess?: (info: { postId?: string; channelSlug?: string }) => void }) {
  const router = useRouter();
  const { t } = useTranslation();
  const { trackCreateSubmit, trackFirstPost } = useAnalytics();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState(initialContent || '');
  const [channelQuery, setChannelQuery] = useState(preselectedChannel || '');
  const [link, setLink] = useState('');
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [showChannelDropdown, setShowChannelDropdown] = useState(false);
  const [linkMeta, setLinkMeta] = useState<OGMetadata | null>(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [mediaPreviews, setMediaPreviews] = useState<string[]>([]);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const [remoteMediaUrls, setRemoteMediaUrls] = useState<string[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [quotedPost, setQuotedPost] = useState<any>(null);
  const [coverArtFile, setCoverArtFile] = useState<File | null>(null);
  const [coverArtPreview, setCoverArtPreview] = useState<string | null>(null);
  const [chatCta, setChatCta] = useState('');
  // Promoted app/agent — renders the post's Open / Chat CTA.
  const [attachedEntity, setAttachedEntity] = useState<AttachedEntity | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Widget publish flow: when a widget is attached, the form publishes a frozen
  // version instead of a plain post. `listed` toggles "Post to channel" vs an
  // "Unlisted link" (public share, no marketplace post). `widgetAttached` lets
  // the user detach and fall back to authoring a normal post.
  const [listed, setListed] = useState(true);
  const [widgetAttached, setWidgetAttached] = useState<boolean>(!!attachWidgetContentId);
  const channelInputRef = useRef<HTMLInputElement>(null);
  const channelDropdownRef = useRef<HTMLDivElement>(null);
  const messageLoaded = useRef(false);

  // Fetch quoted post data by direct DB query via dedicated endpoint
  useEffect(() => {
    if (!quotePostId) return;
    (async () => {
      try {
        const res = await fetch(`/api/posts/${quotePostId}/meta`);
        if (res.ok) {
          const data = await res.json();
          setQuotedPost(data);
        }
      } catch { /* ignore */ }
    })();
  }, [quotePostId]);

  useEffect(() => {
    if (!fromMessageId || messageLoaded.current) return;
    messageLoaded.current = true;

    (async () => {
      try {
        const res = await fetch(`/api/messages/get?id=${fromMessageId}`);
        if (!res.ok) return;
        const { message } = await res.json();

        const textParts: string[] = [];
        const imageUrls: string[] = [];

        if (message.contentItems && Array.isArray(message.contentItems)) {
          for (const item of message.contentItems) {
            if (item.type === 'text' && item.text) {
              const cleaned = item.text
                .replace(/!\[[^\]]*\]\((https?:\/\/[^)]+)\)/g, (_m: string, url: string) => { imageUrls.push(url); return ''; })
                .replace(/\n{3,}/g, '\n\n').trim();
              if (cleaned) textParts.push(cleaned);
            } else if (item.type === 'image') {
              const url = typeof item.image === 'string' ? item.image : item.image?.url;
              if (url) imageUrls.push(url);
            }
          }
        } else if (message.content) {
          const cleaned = message.content
            .replace(/!\[[^\]]*\]\((https?:\/\/[^)]+)\)/g, (_m: string, url: string) => { imageUrls.push(url); return ''; })
            .replace(/\n{3,}/g, '\n\n').trim();
          if (cleaned) textParts.push(cleaned);
        }

        if (message.mediaUrl) {
          imageUrls.push(message.mediaUrl);
        }

        if (textParts.length > 0) setContent(textParts.join('\n\n'));

        if (imageUrls.length > 0) {
          setRemoteMediaUrls(imageUrls);
          setMediaPreviews(imageUrls);

          imageUrls.forEach(async (url, idx) => {
            try {
              const r = await fetch(url);
              if (!r.ok) return;
              const blob = await r.blob();
              const filename = url.split('/').pop()?.split('?')[0] || 'image.jpg';
              const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
              setMediaFiles(prev => {
                const next = [...prev];
                next[idx] = file;
                return next;
              });
            } catch { /* remote URL will be used directly */ }
          });
        }
      } catch (err) {
        console.error('Failed to load message:', err);
      }
    })();
  }, [fromMessageId]);

  useEffect(() => {
    // Fetch channels for dropdown
    fetch('/api/channels?include_categories=true')
      .then(res => res.json())
      .then(data => {
        const allChannels: Channel[] = [];
        for (const section of data.categories || []) {
          allChannels.push({
            id: section.id,
            slug: section.slug,
            name: section.name,
            description: section.description,
            memberCount: section.memberCount,
          });
          for (const ch of section.channels || []) {
            allChannels.push({ 
              id: ch.id, 
              slug: ch.slug, 
              name: ch.name,
              description: ch.description,
              memberCount: ch.memberCount,
            });
          }
        }
        setChannels(allChannels);
        
        // Pre-select channel if provided
        if (preselectedChannel) {
          const found = allChannels.find(ch => ch.slug === preselectedChannel);
          if (found) {
            setSelectedChannel(found);
            setChannelQuery(found.slug);
          }
        }
      })
      .catch(console.error);
  }, [preselectedChannel]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        channelDropdownRef.current &&
        !channelDropdownRef.current.contains(event.target as Node) &&
        channelInputRef.current &&
        !channelInputRef.current.contains(event.target as Node)
      ) {
        setShowChannelDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fuzzy filter channels
  const filteredChannels = channels.filter(ch => {
    if (!channelQuery.trim()) return true;
    const query = channelQuery.toLowerCase();
    return (
      ch.slug.toLowerCase().includes(query) ||
      ch.name.toLowerCase().includes(query) ||
      ch.description?.toLowerCase().includes(query)
    );
  }).slice(0, 8);

  const selectChannel = (ch: Channel) => {
    setSelectedChannel(ch);
    setChannelQuery(ch.slug);
    setShowChannelDropdown(false);
  };

  // Fetch OG metadata when link changes
  useEffect(() => {
    const fetchOG = async () => {
      if (!link.trim()) {
        setLinkMeta(null);
        return;
      }

      // Validate URL
      try {
        new URL(link);
      } catch {
        return;
      }

      setLinkLoading(true);
      try {
        const res = await fetch(`/api/og?url=${encodeURIComponent(link)}`);
        if (res.ok) {
          const data = await res.json();
          setLinkMeta(data);
        } else {
          setLinkMeta(null);
        }
      } catch {
        setLinkMeta(null);
      } finally {
        setLinkLoading(false);
      }
    };

    // Debounce
    const timer = setTimeout(fetchOG, 500);
    return () => clearTimeout(timer);
  }, [link]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length + mediaFiles.length > 4) {
      alert(t('create.maxFilesAllowed'));
      return;
    }

    const validFiles = files.filter(f => {
      const t = f.type;
      if (t.startsWith('image/') || t.startsWith('video/') || t.startsWith('audio/') || t.startsWith('model/')) return true;
      return /\.(glb|gltf|usdz|fbx|obj|stl)$/i.test(f.name);
    });

    setMediaFiles(prev => [...prev, ...validFiles]);

    const newPreviews = validFiles.map(file => URL.createObjectURL(file));
    setMediaPreviews(prev => [...prev, ...newPreviews]);
  };

  const removeMedia = (index: number) => {
    if (mediaPreviews[index]?.startsWith('blob:')) {
      URL.revokeObjectURL(mediaPreviews[index]);
    }
    if (mediaFiles[index]?.type.startsWith('audio/')) {
      if (coverArtPreview?.startsWith('blob:')) URL.revokeObjectURL(coverArtPreview);
      setCoverArtFile(null);
      setCoverArtPreview(null);
    }
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
    setMediaPreviews(prev => prev.filter((_, i) => i !== index));
    setUploadedUrls(prev => prev.filter((_, i) => i !== index));
    setRemoteMediaUrls(prev => prev.filter((_, i) => i !== index));
  };

  type UploadResult = { url: string; mimeType?: string; contentType?: string };

  // Upload a single file via the unified content store
  const uploadFile = async (file: File): Promise<UploadResult | null> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('visibility', 'unlisted');

    const res = await fetch('/api/content/upload', {
      method: 'POST',
      body: formData,
    });

    if (res.ok) {
      const data = await res.json();
      return { url: data.url as string, mimeType: data.mimeType, contentType: data.contentType };
    }
    return null;
  };

  const publishingWidget = widgetAttached && !!attachWidgetContentId;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Widget publish branch: mint a frozen version (listed → marketplace post in
    // the chosen channel; unlisted → public widget, no post). Decoupled from the
    // plain-post path below.
    if (publishingWidget) {
      if (listed && !selectedChannel) return;
      setSubmitting(true);
      try {
        const res = await fetch(`/api/content/${attachWidgetContentId}/publish`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            listed,
            channelSlug: listed ? selectedChannel?.slug : undefined,
            title: title.trim() || undefined,
            content: content.trim() || undefined,
          }),
        });
        if (res.ok) {
          const data = await res.json().catch(() => null);
          trackCreateSubmit('post');
          window.dispatchEvent(new Event('postCreated'));
          onSuccess?.({
            postId: data?.postId ?? undefined,
            channelSlug: listed ? selectedChannel?.slug : undefined,
          });
        } else {
          const err = await res.json().catch(() => ({}));
          alert(err?.error?.message || err?.error || t('create.failedToCreatePost'));
        }
      } catch (error) {
        console.error('Error publishing widget:', error);
        alert(t('create.failedToCreatePost'));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!content.trim() || !selectedChannel) return;

    setSubmitting(true);
    try {
      let mediaUrl: string | undefined;
      let mediaType: string | undefined;
      
      if (mediaFiles.length > 0) {
        setUploadProgress(0);
        const results: Array<{ url: string; mimeType?: string; contentType?: string }> = [];

        for (let i = 0; i < mediaFiles.length; i++) {
          if (mediaFiles[i]) {
            const result = await uploadFile(mediaFiles[i]);
            if (result) results.push(result);
          } else if (remoteMediaUrls[i]) {
            results.push({ url: remoteMediaUrls[i] });
          }
          setUploadProgress(Math.round(((i + 1) / mediaFiles.length) * 100));
        }

        if (results.length > 0) {
          const first = results[0];
          mediaUrl = first.url;
          const ct = first.contentType;
          const mime = (first.mimeType || mediaFiles[0]?.type || '').toLowerCase();
          if (ct === 'model3d' || mime.startsWith('model/')) mediaType = '3d';
          else if (ct === 'video' || mime.startsWith('video/')) mediaType = 'video';
          else if (ct === 'audio' || mime.startsWith('audio/')) mediaType = 'audio';
          else if (mime === 'image/gif') mediaType = 'gif';
          else mediaType = 'image';
        }
        setUploadProgress(null);
      } else if (remoteMediaUrls.length > 0) {
        mediaUrl = remoteMediaUrls[0];
        mediaType = 'image';
      }

      let thumbnailUrl: string | undefined;
      if (coverArtFile && mediaType === 'audio') {
        const thumb = await uploadFile(coverArtFile);
        if (thumb) thumbnailUrl = thumb.url;
      }

      const res = await fetch('/api/posts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim() || undefined,
          content: content.trim(),
          channel_slug: selectedChannel.slug,
          link_url: link.trim() || undefined,
          media_url: mediaUrl,
          media_type: mediaType,
          thumbnail_url: thumbnailUrl,
          quoted_post_id: quotePostId || undefined,
          chat_cta: chatCta.trim() || undefined,
          attached_content_id: attachedEntity?.kind === 'app' ? attachedEntity.id : undefined,
          attached_agent_id: attachedEntity?.kind === 'agent' ? attachedEntity.id : undefined,
        }),
      });

      if (res.ok) {
        trackCreateSubmit('post');
        trackFirstPost();
        window.dispatchEvent(new Event('postCreated'));
        const data = await res.json().catch(() => null);
        if (onSuccess) {
          // Embedded in a workspace dialog — let the host close itself
          // instead of routing the user to /c/[slug] (a page we are
          // retiring in favour of the workspace surface).
          onSuccess({
            postId: data?.post?.id,
            channelSlug: selectedChannel.slug,
          });
        } else {
          router.push(`/c/${selectedChannel.slug}`);
        }
      } else {
        const error = await res.json();
        alert(error.error || t('create.failedToCreatePost'));
      }
    } catch (error) {
      console.error('Error creating post:', error);
      alert(t('create.failedToCreatePost'));
    } finally {
      setSubmitting(false);
      setUploadProgress(null);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Widget attachment — when publishing an owned widget. Shows a
          removable chip + a "Post to channel ↔ Unlisted link" toggle. */}
      {publishingWidget && (
        <div className="space-y-3 rounded-xl border border-primary/30 bg-primary/5 p-3">
          <div className="flex items-center gap-2">
            <Box className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">
              Widget attached{attachWidgetName ? `: ${attachWidgetName}` : ''}
            </span>
            <button
              type="button"
              onClick={() => setWidgetAttached(false)}
              className="ml-auto rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label={t('create.detachApp')}
              title={t('create.publishPlainPost')}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="flex gap-1 rounded-lg bg-muted p-1">
            <button
              type="button"
              onClick={() => setListed(true)}
              className={cn(
                'flex flex-1 items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-medium transition-all',
                listed ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Globe className="h-3.5 w-3.5" />
              {t('create.postToChannel')}
            </button>
            <button
              type="button"
              onClick={() => setListed(false)}
              className={cn(
                'flex flex-1 items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-medium transition-all',
                !listed ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <LinkIcon className="h-3.5 w-3.5" />
              {t('create.unlistedLink')}
            </button>
          </div>
        </div>
      )}
      {/* Channel Select with Autocomplete — hidden for an unlisted widget
          publish (no channel needed). */}
      <div className={cn('relative', publishingWidget && !listed && 'hidden')}>
        <label className="block text-sm font-medium mb-1.5">{t('create.channel')}</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-primary font-mono text-sm">//</span>
          <input
            ref={channelInputRef}
            type="text"
            value={channelQuery}
            onChange={(e) => {
              setChannelQuery(e.target.value);
              setSelectedChannel(null);
              setShowChannelDropdown(true);
            }}
            onFocus={() => setShowChannelDropdown(true)}
            placeholder={t('create.searchChannels')}
            className="input w-full h-10 pl-8"
            autoComplete="off"
          />
          {selectedChannel && (
            <button
              type="button"
              onClick={() => {
                setSelectedChannel(null);
                setChannelQuery('');
                channelInputRef.current?.focus();
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        
        {/* Channel Dropdown */}
        {showChannelDropdown && filteredChannels.length > 0 && (
          <div 
            ref={channelDropdownRef}
            className="absolute z-50 mt-1 w-full bg-background border border-border rounded-lg shadow-lg overflow-hidden"
          >
            {filteredChannels.map((ch) => (
              <button
                key={ch.id}
                type="button"
                onClick={() => selectChannel(ch)}
                className={cn(
                  "w-full px-3 py-2.5 text-left hover:bg-accent transition-colors flex items-start gap-2",
                  selectedChannel?.id === ch.id && "bg-accent"
                )}
              >
                <span className="text-primary font-mono text-sm shrink-0">//</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{ch.slug}</div>
                  {ch.description && (
                    <div className="text-xs text-muted-foreground line-clamp-1">{ch.description}</div>
                  )}
                </div>
                {ch.memberCount !== undefined && (
                  <span className="text-xs text-muted-foreground shrink-0">{ch.memberCount.toLocaleString()}</span>
                )}
              </button>
            ))}
          </div>
        )}
        
        {showChannelDropdown && channelQuery && filteredChannels.length === 0 && (
          <div className="absolute z-50 mt-1 w-full bg-background border border-border rounded-lg shadow-lg p-3 text-center text-sm text-muted-foreground">
            {t('create.noChannels')}
          </div>
        )}
      </div>

      {/* Content */}
      <div>
        <label className="block text-sm font-medium mb-1.5">{t('create.contentLabel')}</label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={t('create.contentPlaceholder')}
          className="input w-full min-h-[120px] py-3 resize-y"
          maxLength={40000}
          required
        />
        <div className="flex items-center justify-between mt-1">
          <p className={cn(
            "text-xs",
            content.length > 280 ? "text-amber-500" : "text-muted-foreground"
          )}>
            {content.length}/280
            {content.length > 280 && <span className="ml-1.5">· {t('create.contentAfter280')}</span>}
          </p>
        </div>
      </div>

      {/* Quoted Post Preview */}
      {quotedPost && (
        <div className="border border-border rounded-lg p-3 bg-muted/30">
          <div className="text-xs text-muted-foreground mb-1">{t('create.quotingLabel')}</div>
          <div className="flex items-center gap-2 mb-1">
            {quotedPost.author && (
              <span className="text-sm font-medium">@{quotedPost.author.username}</span>
            )}
          </div>
          {quotedPost.content && (
            <p className="text-sm line-clamp-3 text-muted-foreground">{quotedPost.content}</p>
          )}
        </div>
      )}

      {/* Title (optional) */}
      <div>
        <label className="block text-sm font-medium mb-1.5">{t('create.titleLabel')}</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('create.titlePlaceholder')}
          className="input w-full h-10"
          maxLength={40}
        />
        <p className="text-xs text-muted-foreground mt-1">{title.length}/40</p>
      </div>

      {/* Link */}
      <div>
        <label className="block text-sm font-medium mb-1.5">
          <LinkIcon className="h-4 w-4 inline mr-1" />
          {t('create.linkLabel')}
        </label>
        <input
          type="url"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder={t('create.linkPlaceholder')}
          className="input w-full h-10"
        />
        
        {/* Link Preview */}
        {linkLoading && (
          <div className="mt-2 p-3 border border-border rounded-lg bg-muted/50 animate-pulse">
            <div className="flex gap-3">
              <div className="w-20 h-20 bg-muted rounded shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-muted rounded w-3/4" />
                <div className="h-3 bg-muted rounded w-full" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </div>
            </div>
          </div>
        )}
        
        {linkMeta && !linkLoading && (
          <div className="mt-2 border border-border rounded-lg overflow-hidden bg-muted/30 hover:bg-muted/50 transition-colors relative">
            {(linkMeta.imageLocal || linkMeta.image) && (
              <div className="aspect-[2/1] bg-muted overflow-hidden">
                <img 
                  src={linkMeta.imageLocal || linkMeta.image} 
                  alt={linkMeta.title || ''}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="p-3">
              {linkMeta.siteName && (
                <p className="text-xs text-muted-foreground mb-1">{linkMeta.siteName}</p>
              )}
              {linkMeta.title && (
                <h4 className="font-medium text-sm line-clamp-2">{linkMeta.title}</h4>
              )}
              {linkMeta.description && (
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{linkMeta.description}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setLink(''); setLinkMeta(null); }}
              className="absolute top-2 right-2 h-6 w-6 rounded-full bg-background/80 flex items-center justify-center hover:bg-background z-10"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Media Upload */}
      <div>
        <label className="block text-sm font-medium mb-1.5">
          <ImageIcon className="h-4 w-4 inline mr-1" />
          {t('create.mediaLabel')}
        </label>
        <div className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
          <input
            type="file"
            accept="image/*,video/*,audio/*,.glb,.gltf,.usdz,.fbx,.obj,.stl,model/*"
            multiple
            onChange={handleFileChange}
            className="hidden"
            id="media-upload"
          />
          <label htmlFor="media-upload" className="cursor-pointer">
            <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              {t('create.mediaDrop')}
            </p>
            <p className="text-xs text-muted-foreground mt-1">{t('create.mediaMax')}</p>
          </label>
        </div>

        {/* Media Previews */}
        {mediaPreviews.length > 0 && (
          <div className="grid grid-cols-2 gap-2 mt-3">
            {mediaPreviews.map((preview, i) => {
              const file = mediaFiles[i];
              const fType = file?.type || '';
              const is3D = fType.startsWith('model/') || /\.(glb|gltf|usdz|fbx|obj|stl)$/i.test(file?.name || '');
              return (
                <div key={i} className={cn("relative rounded-lg overflow-hidden bg-muted", fType.startsWith('audio/') ? 'col-span-2 aspect-auto' : 'aspect-video')}>
                  {fType.startsWith('video/') ? (
                    <video src={preview} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                  ) : fType.startsWith('audio/') ? (
                    <audio src={preview} controls className="w-full" />
                  ) : is3D ? (
                    <div className="flex flex-col items-center justify-center w-full h-full text-muted-foreground gap-2">
                      <Box className="h-10 w-10" />
                      <p className="text-xs px-2 text-center break-all line-clamp-2">{file?.name}</p>
                    </div>
                  ) : (
                    <img src={preview} alt="" className="w-full h-full object-cover" />
                  )}
                  <button
                    type="button"
                    onClick={() => removeMedia(i)}
                    className="absolute top-1 right-1 h-6 w-6 rounded-full bg-background/80 flex items-center justify-center hover:bg-background"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Cover Art (audio only) */}
      {mediaFiles[0]?.type.startsWith('audio/') && (
        <div>
          <label className="block text-sm font-medium mb-1.5">
            <Music className="h-4 w-4 inline mr-1" />
            {t('create.coverArtLabel')}
          </label>
          {coverArtPreview ? (
            <div className="relative inline-block rounded-lg overflow-hidden bg-muted">
              <img src={coverArtPreview} alt="" className="h-32 w-32 object-cover" />
              <button
                type="button"
                onClick={() => {
                  if (coverArtPreview.startsWith('blob:')) URL.revokeObjectURL(coverArtPreview);
                  setCoverArtFile(null);
                  setCoverArtPreview(null);
                }}
                className="absolute top-1 right-1 h-6 w-6 rounded-full bg-background/80 flex items-center justify-center hover:bg-background"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="border-2 border-dashed border-border rounded-lg p-3 text-center hover:border-primary/50 transition-colors max-w-[10rem]">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file || !file.type.startsWith('image/')) return;
                  setCoverArtFile(file);
                  setCoverArtPreview(URL.createObjectURL(file));
                }}
                className="hidden"
                id="create-cover-art-upload"
              />
              <label htmlFor="create-cover-art-upload" className="cursor-pointer">
                <ImageIcon className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
                <p className="text-xs text-muted-foreground">{t('create.coverArtDrop')}</p>
              </label>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-1">{t('create.coverArtHint')}</p>
        </div>
      )}

      {/* Promote an app or agent — binds the post's Open / Chat CTA. Hidden
          in the widget-publish branch, which carries its own attachment. */}
      {!publishingWidget && (
        <div className="space-y-1">
          <label className="text-sm font-medium">{t('create.promoteLabel')}</label>
          {/* Posts may promote ANY listed app/agent (scope=all adds search);
              only channel binding is owner-restricted. */}
          <AttachEntityPicker value={attachedEntity} onChange={setAttachedEntity} scope="all" />
        </div>
      )}

      {/* Advanced */}
      <div>
        <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
          {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          {t('create.advanced')}
        </button>
        {showAdvanced && (
          <div className="mt-3 space-y-1">
            <label className="text-sm font-medium">{t('create.chatCtaLabel')}</label>
            <input
              type="text"
              value={chatCta}
              onChange={(e) => setChatCta(e.target.value)}
              placeholder={t('create.chatCtaPlaceholder')}
              maxLength={280}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <p className="text-xs text-muted-foreground">{t('create.chatCtaHint')}</p>
          </div>
        )}
      </div>

      {/* Upload Progress */}
      {uploadProgress !== null && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{t('create.uploading')}</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={
          submitting ||
          uploadProgress !== null ||
          (publishingWidget
            ? listed && !selectedChannel
            : !content.trim() || !selectedChannel)
        }
        className="btn-primary w-full h-11"
      >
        {submitting ? (
          <>
            <Spinner size="lg" className="mr-2" />
            {uploadProgress !== null ? t('create.uploading') : t('submit.posting')}
          </>
        ) : publishingWidget ? (
          listed ? t('create.publishToChannel') : t('create.createUnlistedLink')
        ) : (
          t('create.postButton')
        )}
      </button>
    </form>
  );
}

// Slug validation state
type SlugStatus = 'idle' | 'checking' | 'available' | 'taken' | 'reserved' | 'invalid';

// Create Channel Form
export function CreateChannelForm({ parentSlug, onSuccess }: { parentSlug?: string; onSuccess?: (info: { channelSlug: string }) => void }) {
  const router = useRouter();
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState(parentSlug ? `${parentSlug}/` : '');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  // Optional app/agent attachment — the channel page then carries the
  // app/agent header + Open/Chat CTA.
  const [attachedEntity, setAttachedEntity] = useState<AttachedEntity | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');
  const [existingChannel, setExistingChannel] = useState<{ slug: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const endsWithSlash = slug.endsWith('/');
  const trimmedSlug = slug.replace(/\/+$/, '');

  // Derive parent from the slug path (everything before last /)
  const lastSlashIdx = trimmedSlug.lastIndexOf('/');
  const derivedParent = lastSlashIdx > 0 ? trimmedSlug.slice(0, lastSlashIdx) : null;
  const isSubchannel = derivedParent !== null;

  // Check slug availability with debounce (only when not ending with /)
  useEffect(() => {
    if (!trimmedSlug || trimmedSlug.length < 2 || endsWithSlash) {
      setSlugStatus('idle');
      setExistingChannel(null);
      return;
    }

    // Validate each segment: lowercase alphanumeric + hyphens, no leading/trailing hyphens
    const segments = trimmedSlug.split('/');
    const segmentPattern = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
    const allValid = segments.every(s => s.length > 0 && segmentPattern.test(s));
    if (!allValid) {
      setSlugStatus('invalid');
      return;
    }

    setSlugStatus('checking');
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/channels/${trimmedSlug}`);
        if (res.ok) {
          const data = await res.json();
          setSlugStatus('taken');
          setExistingChannel({ slug: data.channel.slug, name: data.channel.name });
        } else if (res.status === 404) {
          setSlugStatus('available');
          setExistingChannel(null);
        }
      } catch {
        setSlugStatus('available');
        setExistingChannel(null);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [trimmedSlug, endsWithSlash]);

  const handleSlugChange = (raw: string) => {
    const cleaned = raw
      .toLowerCase()
      .replace(/[^a-z0-9/-]/g, '')
      .replace(/\/{2,}/g, '/');
    setSlug(cleaned);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !trimmedSlug || slugStatus === 'taken' || slugStatus === 'reserved') return;

    setSubmitting(true);
    setError(null);
    
    try {
      const res = await fetch('/api/channels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          slug: trimmedSlug,
          description: description.trim(),
          parentSlug: derivedParent || undefined,
          isPrivate,
          primary_content_id: attachedEntity?.kind === 'app' ? attachedEntity.id : undefined,
          primary_agent_id: attachedEntity?.kind === 'agent' ? attachedEntity.id : undefined,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        window.dispatchEvent(new Event('channelCreated'));
        if (onSuccess) {
          onSuccess({ channelSlug: data.channel.slug });
        } else {
          router.push(`/c/${data.channel.slug}`);
        }
      } else if (res.status === 409 && data.code === 'SLUG_EXISTS') {
        setSlugStatus('taken');
        setExistingChannel(data.existingChannel);
        setError(t('create.channelAlreadyExists'));
      } else if (data.code === 'RESERVED_PREFIX') {
        setSlugStatus('reserved');
        setError(data.error);
      } else {
        setError(data.error || t('create.failedToCreateChannel'));
      }
    } catch (err) {
      console.error('Error creating channel:', err);
      setError(t('create.failedToCreateChannelRetry'));
    } finally {
      setSubmitting(false);
    }
  };

  const slugStatusIcon = () => {
    if (endsWithSlash) return null;
    switch (slugStatus) {
      case 'checking':
        return <Spinner size="md" tone="muted" />;
      case 'available':
        return <span className="text-green-500 text-sm font-medium">{t('create.available')}</span>;
      case 'taken':
        return <span className="text-red-500 text-sm font-medium">{t('create.taken')}</span>;
      case 'reserved':
        return <span className="text-orange-500 text-sm font-medium">{t('create.reserved')}</span>;
      case 'invalid':
        return <span className="text-red-500 text-sm font-medium">{t('create.invalid')}</span>;
      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Error Alert */}
      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-600 text-sm flex items-start gap-2">
          <X className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            {error}
            {existingChannel && (
              <Link 
                href={`/c/${existingChannel.slug}`}
                className="block mt-1 text-primary hover:underline"
              >
                {t('create.goToChannel').replace('{slug}', existingChannel.slug)}
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Channel Name — single unified input */}
      <div>
        <label className="block text-sm font-medium mb-1.5">{t('create.channelName')}</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-primary font-mono text-sm select-none pointer-events-none">
            //
          </span>
          <input
            type="text"
            value={slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            placeholder={t('create.channelNameFullPlaceholder')}
            className={cn(
              "input w-full h-10 pl-8 pr-24 font-mono text-sm",
              !endsWithSlash && slugStatus === 'taken' && "border-red-500 focus:ring-red-500",
              !endsWithSlash && slugStatus === 'available' && "border-green-500 focus:ring-green-500"
            )}
            required
            maxLength={80}
            autoFocus
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {slugStatusIcon()}
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {t('create.channelUrlNesting')}
        </p>

        {/* Existing channel hint — only shown when slug doesn't end with / */}
        {!endsWithSlash && slugStatus === 'taken' && existingChannel && (
          <div className="mt-2 p-2 rounded-lg bg-muted flex items-center justify-between gap-2">
            <span className="text-sm truncate">
              <span className="text-muted-foreground">{t('create.channelExists')}</span>{' '}
              <span className="font-medium">{existingChannel.name}</span>
            </span>
            <Link 
              href={`/c/${existingChannel.slug}`}
              className="text-xs text-primary hover:underline flex items-center gap-1 shrink-0"
            >
              {t('create.visitChannel')} <ArrowLeft className="h-3 w-3 rotate-180" />
            </Link>
          </div>
        )}

        {/* Reserved prefix explanation */}
        {!endsWithSlash && slugStatus === 'reserved' && (
          <div className="mt-2 p-2.5 rounded-lg bg-orange-500/5 border border-orange-500/15 text-sm text-muted-foreground">
            {t('create.domainReservedExplanation').replace('{slug}', trimmedSlug).replace('{domain}', `${trimmedSlug.split('/')[0]}.${trimmedSlug.split('/')[0]}`)}
          </div>
        )}

        {/* Parent path indicator */}
        {isSubchannel && !endsWithSlash && slugStatus !== 'reserved' && (
          <div className="mt-2 p-2 rounded-lg bg-muted/60 text-sm text-muted-foreground">
            {t('create.willBeCreatedInside')}{' '}
            <Link href={`/c/${derivedParent}`} className="text-primary font-medium hover:underline font-mono">//{derivedParent}</Link>
          </div>
        )}
      </div>

      {/* Display Name */}
      <div>
        <label className="block text-sm font-medium mb-1.5">{t('create.displayNameLabel')}</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('create.channelDisplayPlaceholder')}
          className="input w-full h-10"
          required
          maxLength={50}
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium mb-1.5">{t('create.description')}</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('create.descriptionPlaceholder')}
          className="input w-full min-h-[80px] py-3 resize-y"
          maxLength={500}
        />
        <p className="text-xs text-muted-foreground mt-1">{description.length}/500</p>
      </div>

      {/* Attach an app or agent — the channel page then renders the
          app/agent header and Open/Chat CTA. */}
      <div>
        <label className="block text-sm font-medium mb-1.5">{t('create.promoteLabel')}</label>
        <AttachEntityPicker value={attachedEntity} onChange={setAttachedEntity} />
      </div>

      {/* Private Toggle */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="isPrivate"
          checked={isPrivate}
          onChange={(e) => setIsPrivate(e.target.checked)}
          className="h-4 w-4 rounded border-border"
        />
        <label htmlFor="isPrivate" className="text-sm">
          {t('create.private')}
        </label>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={
          !name.trim() || 
          !trimmedSlug || 
          endsWithSlash ||
          slugStatus === 'taken' || 
          slugStatus === 'reserved' ||
          slugStatus === 'invalid' ||
          slugStatus === 'checking' ||
          submitting
        }
        className="btn-primary w-full h-11"
      >
        {submitting ? (
          <>
            <Spinner size="lg" className="mr-2" />
            {t('create.creating')}
          </>
        ) : (
          isSubchannel ? t('create.createSubchannel') : t('create.createChannelButton')
        )}
      </button>

      <p className="text-xs text-center text-muted-foreground">
        {t('create.communityGuidelines')}
      </p>
    </form>
  );
}

// Agent template types
interface EnabledTools extends EnabledToolsConfig {
  schedule?: {
    enabled: boolean;
    frequency: string;
    preferredTime?: string;
    preferredDay?: number;
    timeZone: string;
    backgroundPrompt: string;
  };
}

type TrustRules = string[] | { allow: string[]; deny?: string[] };

interface AgentTemplate {
  id: string;
  label: string;
  labelKey: string;
  icon: any;
  desc: string;
  descKey: string;
  instructions: string;
  model: string;
  enabledTools: Partial<EnabledTools>;
  greetingMessage: string;
  suggestedActions: string[];
  acceptFrom: TrustRules;
  talkTo: TrustRules;
  comingSoon?: boolean;
  relevantCategories: string[];
}


/* The template text lands in the new agent's System Instructions and
   greeting, which the owner reads in agent settings: house copy rules apply
   (visual re-check of 2026-10-07, V3 finding 7: em dashes in both). The public
   template also said "every conversation earns credits for your owner", a
   money claim the credits rules keep out of product copy; it is gone, and so
   is "LLM traffic agent", which only made sense beside it. */
const AGENT_TEMPLATES: AgentTemplate[] = [
  {
    id: 'private',
    label: 'Private Agent',
    labelKey: 'create.privateAgent',
    icon: Bot,
    desc: 'Only you can see it',
    descKey: 'create.privateAgentDesc',
    instructions: 'You are a private personal assistant. Only your owner can interact with you. No one else can see or reach you.\n\nHelp the user with anything they need: answering questions, drafting messages, summarizing information, brainstorming, editing documents, or researching topics.\n\nYou have discovery enabled: use it to find agents, posts, channels, and information across the platform when it would help the user. You can also edit and create documents directly in chat.\n\nBe conversational and concise. Lead with the answer, then explain if needed. When a task is ambiguous, ask one clarifying question rather than guessing. Respect the user\'s time: no filler, no unnecessary preamble.',
    model: 'auto/balanced',
    enabledTools: {
      web_search: { enabled: true },
      code_execution: { enabled: false },
      image_generation: { enabled: false },
      text_editor: { enabled: true },
      discovery: { enabled: true, sources: ['intents', 'agents', 'posts', 'channels', 'users', 'tags'] },
      nli: { enabled: true, maxDepth: 3 },
    },
    greetingMessage: 'Hey! I\'m your private agent. Only you can see me. What can I help with?',
    suggestedActions: ['Summarize my unread messages', 'Find an agent that can help with…', 'Draft a document'],
    acceptFrom: ['everyone'],
    talkTo: ['everyone'],
    relevantCategories: ['productivity', 'communication', 'automation'],
  },
  {
    id: 'public',
    label: 'Public Agent',
    labelKey: 'create.publicAgent',
    icon: Globe,
    desc: 'Available to everyone',
    descKey: 'create.publicAgentDesc',
    instructions: 'You are a public-facing agent. Anyone on the platform can talk to you.\n\nYou can operate as:\n- A **service agent**: perform tasks, answer questions, or provide expertise in a specific domain.\n- A **content/data agent**: share knowledge, curate information, or provide access to specialized content.\n- A **conversation agent**: be helpful and conversational so people want to talk with you.\n\nBe welcoming, professional, and genuinely useful. The more value you provide, the more people will return. If someone asks something outside your scope, suggest they look for a more specialized agent.\n\nNever share your owner\'s private information. Direct complex or sensitive requests to your owner.',
    model: 'auto/balanced',
    enabledTools: {
      web_search: { enabled: true },
      code_execution: { enabled: false },
      image_generation: { enabled: false },
      discovery: { enabled: false, sources: [] },
      nli: { enabled: true, maxDepth: 3 },
    },
    greetingMessage: 'Welcome! I\'m a public agent. Anyone can talk to me. How can I help?',
    suggestedActions: ['What can you help with?', 'Tell me about your services', 'How does this work?'],
    acceptFrom: ['everyone'],
    talkTo: ['family'],
    relevantCategories: ['communication', 'commerce', 'productivity'],
  },
  /* {
    id: 'project',
    label: 'Project Manager',
    icon: Briefcase,
    desc: 'Coordinate with agents',
    instructions: 'You are a project management agent. Only your owner and their selected agents can communicate with you.\n\nWhen someone starts a new project with you:\n1. Ask for the project goal, key milestones, and who\'s involved.\n2. Create a `.md` tracker document with sections for: Goal, Milestones, Tasks, Status, and Blockers.\n3. Keep the tracker updated as work progresses.\n\nOngoing responsibilities:\n- Coordinate tasks across team members and other agents.\n- Track progress, deadlines, and blockers.\n- Summarize status on request and proactively flag risks.\n- Use code execution for data analysis and progress charts when helpful.\n\nBe organized, direct, and action-oriented. Every update should be clear about what\'s done, what\'s next, and what\'s blocked.',
    model: 'auto/balanced',
    enabledTools: {
      web_search: { enabled: true },
      code_execution: { enabled: true },
      image_generation: { enabled: false },
      text_editor: { enabled: true },
      discovery: { enabled: true, sources: ['agents', 'users'] },
      nli: { enabled: true, maxDepth: 5 },
    },
    greetingMessage: 'Project manager online. Tell me about your project and I\'ll create a tracker to get us started.',
    suggestedActions: ['Start a new project', 'Show project status', 'What\'s blocking progress?'],
    acceptFrom: ['family'],
    talkTo: ['family'],
    relevantCategories: ['productivity', 'communication', 'developer', 'automation'],
  }, */
  /* {
    id: 'tutor',
    label: 'Personal Tutor',
    icon: GraduationCap,
    desc: 'Deep-dive learning',
    instructions: 'You are a personal tutor. Explain concepts clearly at the user\'s level, use analogies, and check understanding with follow-up questions. Generate diagrams or code examples when they aid comprehension. Adapt your teaching style based on what works for the learner.',
    model: 'auto/balanced',
    enabledTools: {
      web_search: { enabled: true },
      code_execution: { enabled: true },
      image_generation: { enabled: true },
      discovery: { enabled: false, sources: [] },
      nli: { enabled: false },
    },
    greetingMessage: 'Hi! I\'m your personal tutor. What would you like to learn about?',
    suggestedActions: ['Explain quantum computing simply', 'Help me practice calculus', 'Quiz me on…'],
    acceptFrom: ['everyone'],
    talkTo: ['family'],
    relevantCategories: ['productivity'],
  }, */
  /* {
    id: 'social',
    label: 'Social Media Manager',
    icon: Share2,
    desc: 'Manage your social presence',
    instructions: 'You are a social media manager. Draft posts, suggest content calendars, analyze engagement patterns, and help maintain a consistent brand voice across platforms. Generate images for posts when needed.',
    model: 'auto/balanced',
    enabledTools: {
      web_search: { enabled: true },
      code_execution: { enabled: false },
      image_generation: { enabled: true },
      discovery: { enabled: true, sources: ['posts', 'channels', 'tags'] },
      nli: { enabled: true, maxDepth: 3 },
    },
    greetingMessage: 'Social media manager here! Let\'s grow your audience.',
    suggestedActions: ['Draft a post about…', 'Create a content calendar', 'Analyze my recent engagement'],
    acceptFrom: ['family'],
    talkTo: ['family'],
    comingSoon: true,
    relevantCategories: ['social', 'creative', 'communication'],
  }, */
];

/**
 * Mirrors `slugify` in lib/agents/service.ts, which is server-only and cannot
 * be imported here: the local part the server mints from a name, before any
 * collision suffix (`-2`, `-3`). A preview, so the person sees the handle
 * their agent will answer as while they type; the server's answer is the
 * truth and is what the success state shows. Until 2026-09-27 "Create
 * manually" made up a name (`swift-fox-42`) and asked nothing (e2e report,
 * handoff 2026-09-26).
 */
export function previewAgentHandle(name: string): string {
  return (
    name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s.-]/g, '')
      .replace(/[\s_]+/g, '-')
      .replace(/^[.-]+|[.-]+$/g, '') || 'agent'
  );
}

/** The two limits `POST /api/agents` puts on `name` (`z.string().min(1).max(100)`). */
export const AGENT_NAME_MAX_LENGTH = 100;
export function validateAgentName(name: string): 'required' | 'tooLong' | null {
  const trimmed = name.trim();
  if (!trimmed) return 'required';
  if (trimmed.length > AGENT_NAME_MAX_LENGTH) return 'tooLong';
  return null;
}

/**
 * How long "Creating agent…" may spin before it is called off. The create
 * route is a handful of writes and one address mint and answers in well
 * under a second on the local cluster, so half a minute is a stuck request,
 * not a slow one. The spinner had no end at all before 2026-09-27.
 */
export const CREATE_AGENT_TIMEOUT_MS = 30_000;

/** Where "create a local agent" goes: `/local`, as the view for running your own agent. */
export const LOCAL_AGENT_HREF = '/local?context=agent';

export function CreateAgentForm({ onSuccess }: { onSuccess?: (info: { agentUsername: string; agentId: string }) => void } = {}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { openHref, close: closeFullscreen, isOpen: inWorkspaceDialog } = useFullscreenNav();
  const { addItem } = useWorkspaceActions();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agentName, setAgentName] = useState('');
  const [nameError, setNameError] = useState<'required' | 'tooLong' | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  // The agent the last submit made, when this form owns the outcome (no
  // `onSuccess`). It used to hard-navigate to `/u/<name>?tab=settings`,
  // which the profile route rewrites into the preview dialog and DROPS the
  // tab, so the person landed on a profile card with nothing saying the
  // agent was theirs to set up. Now the form says so and offers the settings.
  const [created, setCreated] = useState<{ username: string; id: string } | null>(null);

  const createDefaultAgent = async () => {
    const problem = validateAgentName(agentName);
    if (problem) {
      setNameError(problem);
      nameInputRef.current?.focus();
      return;
    }
    setNameError(null);
    setSubmitting(true);
    setError(null);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), CREATE_AGENT_TIMEOUT_MS);
    try {
      const tmpl = AGENT_TEMPLATES.find(t => t.id === 'private') ?? AGENT_TEMPLATES[0];
      const tools = { ...tmpl.enabledTools };
      if (tools.schedule?.timeZone === 'AUTO') {
        tools.schedule = { ...tools.schedule, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone };
      }

      const res = await fetch('/api/agents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          name: agentName.trim(),
          namespaced: true,
          // No `description`. This used to seed `tmpl.desc`, which is chooser
          // copy ("Only you can see it" / "Available to everyone"): it labels
          // the template card in the picker, it does not describe the agent.
          // Every agent made through this button therefore shipped with a
          // visibility label as its bio, and that field is what the public
          // listing and profile layouts hand to `metadata.description`, so it
          // surfaced verbatim as the meta description and search snippet of
          // real listings. Better empty than wrong: those layouts already fall
          // back to generic copy. See seo-audit-2026-08-22 item 3.6.
          instructions: tmpl.instructions,
          model: DEFAULT_PLATFORM_MODEL,
          enabledTools: tools,
          greetingMessage: t('create.defaultGreeting'),
          suggestedActions: [
            t('create.defaultActionUpdateSettings'),
            t('create.defaultActionMonetize'),
            t('create.defaultActionAssignTask'),
            t('create.defaultActionWhatElse'),
          ],
          acceptFrom: tmpl.acceptFrom,
          talkTo: tmpl.talkTo,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || t('create.failedToCreateAgent'));
        return;
      }

      const data = await res.json();
      window.dispatchEvent(new Event('agentCreated'));

      try {
        await Promise.all([
          fetch(`/api/users/${data.agent.username}/follow`, { method: 'POST' }),
          fetch('/api/users/me/starred', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ itemType: 'user', itemId: data.agent.id }),
          }),
        ]);
        window.dispatchEvent(new CustomEvent('followChanged', { detail: { type: 'agent' } }));
        window.dispatchEvent(new Event('starChanged'));
      } catch {
        // Auto-follow/star failed — non-blocking
      }

      try {
        const chatRes = await fetch('/api/messages', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'dm', participantIds: [data.agent.id] }),
        });
        if (chatRes.ok) {
          const { chatId } = await chatRes.json();
          localStorage.setItem('chat-sidebar-open', 'true');
          localStorage.setItem('chat-sidebar-view', 'chat');
          localStorage.setItem('chat-sidebar-chat-id', chatId);
        }
      } catch {
        // Chat creation failed — still navigate to profile
      }

      if (onSuccess) {
        // Embedded by a caller that owns the outcome: hand it over.
        onSuccess({
          agentUsername: data.agent.username,
          agentId: data.agent.id,
        });
      } else {
        setCreated({ username: data.agent.username, id: data.agent.id });
      }
    } catch (err) {
      // `fetch` rejects an aborted request with a DOMException named
      // AbortError; read the name rather than the class, which differs
      // between browsers and test runtimes.
      const aborted = (err as { name?: string } | null)?.name === 'AbortError';
      setError(aborted ? t('create.createTimedOut') : t('create.networkError'));
    } finally {
      clearTimeout(timer);
      setSubmitting(false);
    }
  };

  const handlePreview = `${user?.username ? `${user.username}.` : ''}${previewAgentHandle(agentName)}`;

  const addCreatedToCanvas = async () => {
    if (!created) return;
    try {
      await addItem({
        type: 'agent',
        refId: created.id,
        state: {
          cache: {
            username: created.username,
            displayName: agentName.trim() || created.username,
            avatarUrl: null,
          },
        },
      });
      closeFullscreen();
    } catch {
      setError(t('personalAgent.openFailed'));
    }
  };

  // "Create with AI" drops the caller's OWN personal agent on the canvas,
  // prefilled, the way `search-everything.tsx` and `create-with-dialog.tsx`
  // do. Two earlier shapes failed silently. `router.push('/u/robutler.factory
  // ?prefill=…')`: the factory is retired (2026-09-05) and `/u/[username]`
  // redirects a signed-in user to the workspace and DROPS the query. Then
  // `chatSidebar.openDraftChat`: this form is mounted only by the canvas
  // shell (`fullscreen-nav.tsx`, `/create-agent` is a redirect stub), and the
  // canvas layout renders no `ChatSidebar`, so the draft went into provider
  // state nothing displayed; even where the sidebar exists it carries no
  // `x-workspace-id` / `x-workspace-tab-id`, so the agent's `open_app` had no
  // tab to place on (review finding B1). The AgentNode stamps both headers
  // on every send. The agent is resolved through `GET /api/agents/personal`,
  // never reconstructed from the username, and BEFORE `addItem` so a failed
  // lookup writes nothing; a guest has none and gets the signup dialog.
  const handleCreateWithAi = async () => {
    setError(null);
    try {
      const lookup = await fetchPersonalAgent();
      if (!lookup.agent) {
        if (lookup.guest) {
          openHref('/signup');
          return;
        }
        setError(t('personalAgent.notReady'));
        return;
      }
      const agent = lookup.agent;
      await addItem({
        type: 'agent',
        refId: agent.id,
        state: {
          // Same footprint as the other two authoring funnels: the composer
          // holds a spec, so the tile needs room for it and the welcome line.
          width: 450,
          height: 500,
          cache: {
            username: agent.username,
            displayName: agent.displayName ?? agent.username,
            avatarUrl: agent.avatarUrl ?? null,
            prefillText: t('create.factoryPrefill'),
          },
        },
      });
      // The tile is on the canvas behind this full-screen form; get out of
      // its way.
      closeFullscreen();
    } catch {
      setError(t('personalAgent.openFailed'));
    }
  };

  return (
    <div className="max-w-lg mx-auto space-y-4">
      {created ? (
        <div className="card p-6 space-y-4" data-testid="create-agent-success">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
              <Check className="h-5 w-5 text-emerald-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">{t('create.agentReady', { handle: created.username })}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t('create.agentReadyDesc')}</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => openHref(`/agent-settings?username=${encodeURIComponent(created.username)}`)}
              className="btn-primary text-xs"
            >
              {t('create.openAgentSettings')}
            </button>
            <button type="button" onClick={() => { void addCreatedToCanvas(); }} className="btn-secondary text-xs">
              {t('standalone.addToWorkspace')}
            </button>
            <button
              type="button"
              onClick={() => { setCreated(null); setAgentName(''); setError(null); }}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              {t('create.createAnotherAgent')}
            </button>
          </div>
          {error && <p role="alert" className="text-xs text-red-500">{error}</p>}
        </div>
      ) : (
        <div className="card p-6 space-y-3">
          <button
            type="button"
            onClick={() => { void handleCreateWithAi(); }}
            disabled={submitting}
            className="w-full rounded-xl border border-purple-500/30 bg-purple-500/5 hover:bg-purple-500/10 transition-colors p-4 text-left flex items-start gap-3 group"
          >
            <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center shrink-0 group-hover:bg-purple-500/20 transition-colors">
              <Sparkles className="h-5 w-5 text-purple-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-purple-500">{t('create.createWithAi')}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t('create.createWithAiDesc')}</div>
            </div>
          </button>

          <div className="space-y-1.5 pt-1">
            <label htmlFor="create-agent-name" className="block text-xs font-medium">{t('create.agentName')}</label>
            <input
              id="create-agent-name"
              ref={nameInputRef}
              type="text"
              value={agentName}
              onChange={(e) => {
                setAgentName(e.target.value);
                if (nameError) setNameError(validateAgentName(e.target.value));
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void createDefaultAgent(); } }}
              placeholder={t('create.agentNamePlaceholder')}
              maxLength={AGENT_NAME_MAX_LENGTH}
              aria-invalid={nameError ? true : undefined}
              disabled={submitting}
              className="input w-full text-sm"
            />
            {nameError ? (
              <p role="alert" className="text-xs text-red-500">
                {nameError === 'required' ? t('create.agentNameRequired') : t('create.agentNameTooLong')}
              </p>
            ) : agentName.trim() ? (
              <p className="text-xs text-muted-foreground">{t('create.handlePreview', { handle: handlePreview })}</p>
            ) : null}
          </div>

          <button
            type="button"
            onClick={createDefaultAgent}
            disabled={submitting}
            className="w-full rounded-xl border border-border hover:border-primary/30 hover:bg-muted/50 transition-colors p-4 text-left flex items-start gap-3 group disabled:opacity-60"
          >
            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
              {submitting ? <Spinner size="lg" /> : <Bot className="h-5 w-5 text-muted-foreground" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">{t('create.createManually')}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {submitting ? t('create.creatingAgentProgress') : t('create.createManuallyDesc')}
              </div>
            </div>
          </button>

          {error && <p role="alert" className="text-xs text-red-500">{error}</p>}
        </div>
      )}

      {/* The local-agent view, not the generic `/local` landing: that one is
          titled "Install app" in the workspace and leads with installing the
          web app (portal visual pass, 2026-09-28). Inside the workspace dialog
          it opens on the dialog stack, so Back returns to this form. */}
      <p className="text-xs text-center text-muted-foreground">
        {t('create.orCreateLocal')}
        <Link
          href={LOCAL_AGENT_HREF}
          data-testid="create-local-agent-link"
          onClick={(e) => {
            if (!inWorkspaceDialog || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            e.preventDefault();
            openHref(LOCAL_AGENT_HREF);
          }}
          className="text-primary hover:underline"
        >
          {t('create.createLocalAgent')}
        </Link>
      </p>
    </div>
  );
}

